/**
 * services/sync/SyncOperationService.ts
 * Operation logging infrastructure for operation-based sync
 *
 * Singleton service that manages the local operation log for sync.
 * Provides methods for:
 * - Logging operations with monotonic sequence numbers
 * - Querying pending (unsynced) operations
 * - Marking operations as synced after export
 * - Purging operations after acknowledgment
 *
 * This service is the foundation of the operation-based sync architecture,
 * enabling field-level change tracking and conflict-free synchronization.
 *
 * Usage:
 * ```ts
 * // Direct class access (for non-React code like SyncEngine)
 * import { SyncOperationService } from '@/services/sync/SyncOperationService';
 * const service = SyncOperationService.getInstance();
 *
 * // Or use the lazy getter function
 * import { getSyncOperationService } from '@/services/sync/SyncOperationService';
 * const service = getSyncOperationService();
 *
 * // React hook access (for components)
 * import { useSyncOperationService } from '@/services/sync/SyncOperationService';
 * const service = useSyncOperationService();
 * ```
 */

import Dexie from 'dexie';
import { db } from '@/lib/db';
import { getOrCreateDeviceId } from '@/utils/deviceDetection';
import { generateUUID } from '@/utils/uuid';
import type { SyncState } from '@/types/sync/SyncState';
import type { StoredSyncOperation } from '@/types/sync/SyncOperation';
import type { SyncTableName } from '@/constants/syncTables';
import { isImportContext } from '@/services/sync/SyncImportContext';

// ===== Input Parameter Types =====

/**
 * Parameters for logging a 'set' operation (field update)
 *
 * @example
 * ```ts
 * const params: LogSetOperationParams = {
 *   table: 'trades',
 *   recordId: 'trade-123',
 *   op: 'set',
 *   field: 'notes',
 *   value: 'Updated trade notes'
 * };
 * ```
 */
export type LogSetOperationParams = {
  /** Target table name */
  table: SyncTableName;
  /** Target record ID */
  recordId: string;
  /** Operation type discriminant */
  op: 'set';
  /** Field name to update */
  field: string;
  /** New value for the field */
  value: unknown;
  /** Optional batch ID for grouping related operations */
  batchId?: string;
};

/**
 * Parameters for logging a 'clear' operation (field removal)
 *
 * @example
 * ```ts
 * const params: LogClearOperationParams = {
 *   table: 'trades',
 *   recordId: 'trade-123',
 *   op: 'clear',
 *   field: 'notes'
 * };
 * ```
 */
export type LogClearOperationParams = {
  /** Target table name */
  table: SyncTableName;
  /** Target record ID */
  recordId: string;
  /** Operation type discriminant */
  op: 'clear';
  /** Field name to clear */
  field: string;
  /** Optional batch ID for grouping related operations */
  batchId?: string;
};

/**
 * Parameters for logging a 'delete' operation (record deletion)
 *
 * @example
 * ```ts
 * const params: LogDeleteOperationParams = {
 *   table: 'trades',
 *   recordId: 'trade-123',
 *   op: 'delete'
 * };
 * ```
 */
export type LogDeleteOperationParams = {
  /** Target table name */
  table: SyncTableName;
  /** Target record ID */
  recordId: string;
  /** Operation type discriminant */
  op: 'delete';
  /** Optional batch ID for grouping related operations */
  batchId?: string;
};

/**
 * Union type for all operation logging parameters
 *
 * Discriminated by the `op` field:
 * - 'set': Update a field value
 * - 'clear': Remove a field value
 * - 'delete': Delete entire record
 */
export type LogOperationParams =
  | LogSetOperationParams
  | LogClearOperationParams
  | LogDeleteOperationParams;

/**
 * Operation parameters for use with logBatch()
 *
 * Same as LogOperationParams but without batchId - logBatch() always
 * generates its own batchId to ensure all ops in the batch share it.
 *
 * For manual batch control across separate calls, use logOperation()
 * with a caller-provided batchId instead.
 */
export type LogBatchOperationParams =
  | Omit<LogSetOperationParams, 'batchId'>
  | Omit<LogClearOperationParams, 'batchId'>
  | Omit<LogDeleteOperationParams, 'batchId'>;

/**
 * SyncOperationService
 *
 * Singleton service for managing the local operation log.
 * Handles sequence number generation, operation persistence,
 * and lifecycle management (sync marking, purging).
 *
 * @example
 * ```ts
 * const service = SyncOperationService.getInstance();
 * await service.logOperation({ table: 'trades', recordId: '123', op: 'set', field: 'notes', value: 'New note' });
 * ```
 */
export class SyncOperationService {
  /**
   * Singleton instance
   */
  private static instance: SyncOperationService | null = null;

  /**
   * Cached device ID for this browser/device.
   * Stable within a session (stored in localStorage).
   */
  private deviceId: string;

  /**
   * Private constructor to enforce singleton pattern.
   * Caches the device ID on instantiation.
   */
  private constructor() {
    this.deviceId = getOrCreateDeviceId();
  }

  /**
   * Get the singleton instance of SyncOperationService.
   *
   * Creates the instance on first call (lazy initialization).
   * All subsequent calls return the same instance.
   *
   * @returns The singleton SyncOperationService instance
   *
   * @example
   * ```ts
   * const service = SyncOperationService.getInstance();
   * ```
   */
  static getInstance(): SyncOperationService {
    if (!SyncOperationService.instance) {
      SyncOperationService.instance = new SyncOperationService();
    }
    return SyncOperationService.instance;
  }

  /**
   * Internal method to increment sequence number within an active transaction.
   *
   * MUST be called within a Dexie transaction that includes db.sync_state.
   * This allows logOperation() and logBatch() to atomically increment seq
   * and write operations in a single transaction.
   *
   * First-run defaults match SyncEngine semantics (see SyncEngine.ts:1645-1652):
   * - lastSyncedAt: epoch (never synced)
   * - syncProvider: 'local' (placeholder)
   *
   * @returns The next sequence number (>=1)
   * @internal
   */
  private async _incrementSeqInTransaction(): Promise<number> {
    // Read current sync state for this device
    let syncState: SyncState | undefined = await db.sync_state.get(this.deviceId);
    const now = new Date().toISOString();

    if (!syncState) {
      // First-run: create sync_state record with defaults
      // Matches SyncEngine first-run semantics (see SyncEngine.ts:1645-1652)
      syncState = {
        id: this.deviceId,
        createdAt: now,
        updatedAt: now,
        lastSyncedAt: new Date(0).toISOString(), // Epoch = sync everything (avoid "already synced" state)
        lastSyncDirection: 'export',
        pendingChanges: 0,
        syncProvider: 'local', // Placeholder until SyncEngine configures actual provider
        lastSeqIssued: 0,
        lastAppliedSeqByDevice: {},
      };
    }

    // Increment sequence number (0 → 1 on first call)
    const newSeq = (syncState.lastSeqIssued ?? 0) + 1;

    // Write back with updated seq and timestamp, preserving all other fields
    await db.sync_state.put({
      ...syncState,
      lastSeqIssued: newSeq,
      updatedAt: now,
    });

    return newSeq;
  }

  /**
   * Get the next monotonically increasing sequence number for this device.
   *
   * Transaction-safe: Uses a Dexie read-write transaction to ensure
   * concurrent callers (including across browser tabs) receive unique
   * sequence numbers. IndexedDB transactions are serialized at the
   * browser level, preventing race conditions.
   *
   * Sequence numbers:
   * - Start at 1 (not 0) for the first operation
   * - Are unique per device and never reused (even across browser sessions)
   * - Are persisted in sync_state.lastSeqIssued to survive restarts
   * - May have gaps if operation logging fails after seq increment
   *
   * @returns Promise resolving to the next sequence number (>=1)
   * @throws Error if database transaction fails (caller should handle)
   *
   * @example
   * ```ts
   * const seq = await service.getNextSeq();
   * console.log(seq); // 1 on first call, 2 on second, etc.
   * ```
   */
  async getNextSeq(): Promise<number> {
    try {
      return await db.transaction('rw', db.sync_state, async () => {
        return await this._incrementSeqInTransaction();
      });
    } catch (error) {
      console.error('[SyncOperationService] getNextSeq failed:', error);
      throw error;
    }
  }

  /**
   * Log a single sync operation to the local operation log.
   *
   * Atomically increments the sequence number and writes the operation
   * in a single transaction, ensuring consistency even with concurrent calls.
   *
   * **Import suppression**: Returns `null` without logging when called within
   * an import transaction (marked with `isImport: true`). This prevents remote
   * operations from being re-logged as local operations during sync imports.
   *
   * Auto-generated fields:
   * - `opId`: UUID generated internally
   * - `seq`: Monotonic sequence number from `_incrementSeqInTransaction()`
   * - `deviceId`: This device's ID
   * - `timestamp`: Current ISO timestamp
   * - `synced`: Always `false` for new operations
   *
   * @param params - Operation parameters (table, recordId, op type, field/value)
   * @returns The created StoredSyncOperation, or `null` if suppressed during import
   * @throws Error if database transaction fails
   *
   * @example
   * ```ts
   * // Log a field update
   * const op = await service.logOperation({
   *   table: 'trades',
   *   recordId: 'trade-123',
   *   op: 'set',
   *   field: 'notes',
   *   value: 'Updated notes'
   * });
   *
   * // Log a record deletion
   * const deleteOp = await service.logOperation({
   *   table: 'trades',
   *   recordId: 'trade-456',
   *   op: 'delete'
   * });
   * ```
   */
  async logOperation(params: LogOperationParams): Promise<StoredSyncOperation | null> {
    // Suppress op logging during import transactions
    if (isImportContext()) {
      return null;
    }

    try {
      return await db.transaction('rw', [db.sync_state, db.sync_operations], async () => {
        const seq = await this._incrementSeqInTransaction();
        const opId = generateUUID();
        const timestamp = new Date().toISOString();

        // Build the stored operation based on op type
        let storedOp: StoredSyncOperation;

        if (params.op === 'set') {
          storedOp = {
            opId,
            seq,
            deviceId: this.deviceId,
            timestamp,
            table: params.table,
            recordId: params.recordId,
            op: 'set',
            field: params.field,
            value: params.value,
            synced: 0,
            ...(params.batchId !== undefined && { batchId: params.batchId }),
          };
        } else if (params.op === 'clear') {
          storedOp = {
            opId,
            seq,
            deviceId: this.deviceId,
            timestamp,
            table: params.table,
            recordId: params.recordId,
            op: 'clear',
            field: params.field,
            synced: 0,
            ...(params.batchId !== undefined && { batchId: params.batchId }),
          };
        } else {
          // params.op === 'delete'
          storedOp = {
            opId,
            seq,
            deviceId: this.deviceId,
            timestamp,
            table: params.table,
            recordId: params.recordId,
            op: 'delete',
            synced: 0,
            ...(params.batchId !== undefined && { batchId: params.batchId }),
          };
        }

        await db.sync_operations.add(storedOp);
        return storedOp;
      });
    } catch (error) {
      console.error('[SyncOperationService] logOperation failed:', error);
      throw error;
    }
  }

  /**
   * Log multiple sync operations atomically as a batch.
   *
   * All operations in the batch:
   * - Share the same auto-generated `batchId`
   * - Share the same `timestamp` (single logical moment)
   * - Have sequential `seq` numbers (one increment per operation)
   * - Are written atomically (all-or-nothing)
   *
   * **Import suppression**: Returns empty array `[]` without logging when called
   * within an import transaction (marked with `isImport: true`). This prevents
   * remote operations from being re-logged as local operations during sync imports.
   *
   * Use this for multi-field updates that should be treated as a single
   * logical change. For manual batch control across separate calls,
   * use `logOperation()` with a caller-provided `batchId` instead.
   *
   * @param ops - Array of operation parameters (must be non-empty, no batchId allowed)
   * @returns Array of created StoredSyncOperations in input order, or `[]` if suppressed during import
   * @throws Error if ops array is empty or database transaction fails
   *
   * @example
   * ```ts
   * // Update multiple fields atomically
   * const ops = await service.logBatch([
   *   { table: 'trades', recordId: 'trade-123', op: 'set', field: 'notes', value: 'Note' },
   *   { table: 'trades', recordId: 'trade-123', op: 'set', field: 'rating', value: 5 },
   *   { table: 'trades', recordId: 'trade-123', op: 'clear', field: 'tempFlag' }
   * ]);
   * // All ops share the same batchId, timestamp, and have sequential seq numbers
   * ```
   */
  async logBatch(ops: LogBatchOperationParams[]): Promise<StoredSyncOperation[]> {
    // Suppress op logging during import transactions
    if (isImportContext()) {
      return [];
    }

    if (ops.length === 0) {
      throw new Error('[SyncOperationService] logBatch requires at least one operation');
    }

    try {
      return await db.transaction('rw', [db.sync_state, db.sync_operations], async () => {
        const batchId = generateUUID();
        const timestamp = new Date().toISOString(); // Single timestamp for entire batch
        const storedOps: StoredSyncOperation[] = [];

        for (const params of ops) {
          const seq = await this._incrementSeqInTransaction();
          const opId = generateUUID();

          // Build the stored operation based on op type
          let storedOp: StoredSyncOperation;

          if (params.op === 'set') {
            storedOp = {
              opId,
              seq,
              deviceId: this.deviceId,
              timestamp,
              table: params.table,
              recordId: params.recordId,
              op: 'set',
              field: params.field,
              value: params.value,
              batchId,
              synced: 0,
            };
          } else if (params.op === 'clear') {
            storedOp = {
              opId,
              seq,
              deviceId: this.deviceId,
              timestamp,
              table: params.table,
              recordId: params.recordId,
              op: 'clear',
              field: params.field,
              batchId,
              synced: 0,
            };
          } else {
            // params.op === 'delete'
            storedOp = {
              opId,
              seq,
              deviceId: this.deviceId,
              timestamp,
              table: params.table,
              recordId: params.recordId,
              op: 'delete',
              batchId,
              synced: 0,
            };
          }

          storedOps.push(storedOp);
        }

        // Bulk write all operations
        await db.sync_operations.bulkAdd(storedOps);
        return storedOps;
      });
    } catch (error) {
      console.error('[SyncOperationService] logBatch failed:', error);
      throw error;
    }
  }

  // ===== Query & Lifecycle =====

  /**
   * Get all pending (unsynced) operations for this device.
   *
   * Returns operations where `synced === false` for the current device,
   * ordered by `seq` ascending. Uses the `[synced+deviceId+seq]` compound
   * index for efficient querying.
   *
   * Note: Only local operations are stored in `sync_operations`. Imported
   * operations from other devices are applied directly and never stored here.
   *
   * @returns Array of pending operations ordered by seq, empty if none
   * @throws Error if database query fails
   *
   * @example
   * ```ts
   * const pending = await service.getPendingOps();
   * console.log(`${pending.length} operations to export`);
   * // Operations are ordered by seq for deterministic export
   * ```
   */
  async getPendingOps(): Promise<StoredSyncOperation[]> {
    try {
      // Use compound index [synced+deviceId+seq] for efficient query
      // Results are automatically ordered by seq (last component of compound key)
      // NO .sortBy() needed - compound index guarantees seq ordering
      // Note: Use 0 instead of false - booleans are not valid IndexedDB keys
      return await db.sync_operations
        .where('[synced+deviceId+seq]')
        .between(
          [0, this.deviceId, Dexie.minKey],
          [0, this.deviceId, Dexie.maxKey]
        )
        .toArray();
    } catch (error) {
      console.error('[SyncOperationService] getPendingOps failed:', error);
      throw error;
    }
  }

  /**
   * Mark operations as synced after successful export.
   *
   * Updates `synced: true` for all specified operation IDs. This is the
   * first phase of the two-phase lifecycle: mark as synced immediately
   * after export file is written, then optionally purge later for cleanup.
   *
   * @param opIds - Array of operation IDs to mark as synced
   * @returns Number of operations actually modified (may be less than opIds.length
   *          if some ops were already synced or don't exist)
   * @throws Error if database operation fails
   *
   * @example
   * ```ts
   * const pending = await service.getPendingOps();
   * // ... export operations ...
   * const marked = await service.markOpsSynced(pending.map(op => op.opId));
   * console.log(`Marked ${marked} operations as synced`);
   * ```
   */
  async markOpsSynced(opIds: string[]): Promise<number> {
    // Early return for empty array (valid no-op case)
    if (opIds.length === 0) {
      return 0;
    }

    try {
      const count = await db.sync_operations
        .where('opId')
        .anyOf(opIds)
        .modify({ synced: 1 });

      // Diagnostic warning if some ops were missing/already synced
      if (count < opIds.length) {
        console.warn(
          `[SyncOperationService] markOpsSynced: only ${count} of ${opIds.length} ops modified (some may be missing or already synced)`
        );
      }

      return count;
    } catch (error) {
      console.error('[SyncOperationService] markOpsSynced failed:', error);
      throw error;
    }
  }

  /**
   * Permanently delete operations from the local operation log.
   *
   * This is the optional second phase of the lifecycle: after operations
   * are marked as synced and export is acknowledged, they can be purged
   * for cleanup. Marked operations can also persist for debugging if desired.
   *
   * @param opIds - Array of operation IDs to delete
   * @returns Number of operations actually deleted (may be less than opIds.length
   *          if some ops were already purged or don't exist)
   * @throws Error if database operation fails
   *
   * @example
   * ```ts
   * // After export acknowledgment, optionally clean up
   * const purged = await service.purgeOps(exportedOpIds);
   * console.log(`Purged ${purged} operations`);
   * ```
   */
  async purgeOps(opIds: string[]): Promise<number> {
    // Early return for empty array (valid no-op case)
    if (opIds.length === 0) {
      return 0;
    }

    try {
      const count = await db.sync_operations
        .where('opId')
        .anyOf(opIds)
        .delete();

      // Diagnostic warning if some ops were missing/already purged
      if (count < opIds.length) {
        console.warn(
          `[SyncOperationService] purgeOps: only ${count} of ${opIds.length} ops deleted (some may be missing or already purged)`
        );
      }

      return count;
    } catch (error) {
      console.error('[SyncOperationService] purgeOps failed:', error);
      throw error;
    }
  }
}

/**
 * Lazy getter function for SyncOperationService singleton.
 *
 * Use this for non-React code when you prefer a function call over
 * direct class access. Instantiation is deferred until first call.
 *
 * @returns The SyncOperationService singleton instance
 *
 * @example
 * ```ts
 * import { getSyncOperationService } from '@/services/sync/SyncOperationService';
 * const service = getSyncOperationService();
 * const pending = await service.getPendingOps();
 * ```
 */
export function getSyncOperationService(): SyncOperationService {
  return SyncOperationService.getInstance();
}

/**
 * React hook for accessing SyncOperationService.
 *
 * Provides the singleton instance for use in React components.
 * This is a convenience wrapper that ensures consistent access patterns.
 *
 * @returns The SyncOperationService singleton instance
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const syncOpService = useSyncOperationService();
 *   // Use syncOpService methods...
 * }
 * ```
 */
export function useSyncOperationService(): SyncOperationService {
  return SyncOperationService.getInstance();
}
